import { z } from "zod";

export const lojaSchema = z.object({
  nome: z
    .string()
    .min(1, "Nome é obrigatório")
    .max(255, "Nome deve ter no máximo 255 caracteres"),
  endereco: z
    .string()
    .max(255, "Endereço deve ter no máximo 255 caracteres")
    .optional(),
  telefone: z
    .string()
    .length(15, "Telefone deve ter 15 caracteres (com máscara)")
    .optional(),
  cnpj: z
    .string()
    .length(18, "CNPJ deve ter 18 caracteres (com máscara)")
    .optional(),
  usaIntegracaoPedidos: z.boolean().optional(),
  roteirizacaoAutomaticaAtiva: z.boolean().optional(),
  distanciaMaximaCasamentoKm: z
    .number({ error: "Distância deve ser um número" })
    .min(0.1, "Distância mínima: 0,1 km")
    .max(50, "Distância máxima: 50 km")
    .optional(),
  aguardarCasamentoMinutos: z
    .number({ error: "Tempo de espera deve ser um número" })
    .min(0, "Tempo mínimo: 0 min")
    .max(30, "Tempo máximo: 30 min")
    .optional(),
  lojaLatitude: z
    .number({ error: "Latitude deve ser um número" })
    .min(-90)
    .max(90)
    .nullable()
    .optional(),
  lojaLongitude: z
    .number({ error: "Longitude deve ser um número" })
    .min(-180)
    .max(180)
    .nullable()
    .optional(),
  regrasLoteRoteirizacao: z
    .array(
      z.object({
        inicio: z
          .string()
          .regex(/^\d{2}:\d{2}$/, "Horário deve estar no formato HH:mm"),
        fim: z
          .string()
          .regex(/^\d{2}:\d{2}$/, "Horário deve estar no formato HH:mm"),
        maxComandas: z
          .number()
          .int()
          .min(1, "Mínimo 1 comanda")
          .max(3, "Máximo 3 comandas"),
      })
    )
    .nullable()
    .optional(),
});

export const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(1, "Senha é obrigatória"),
});

export const usuarioCreateSchema = z.object({
  nome: z
    .string()
    .min(1, "Nome é obrigatório")
    .max(255, "Nome deve ter no máximo 255 caracteres"),
  email: z
    .string()
    .email("Email inválido")
    .max(255, "Email deve ter no máximo 255 caracteres"),
  senha: z.string().min(6, "Senha deve ter no mínimo 6 caracteres"),
  lojaId: z.number().int("Loja deve ser um número inteiro").optional(),
  roleId: z.number().int("Permissão deve ser um número inteiro"),
  isConferenteBase: z.boolean().optional(),
  isSupervisorProducao: z.boolean().optional(),
  setorIds: z.array(z.number().int()).optional(),
});

export const usuarioUpdateSchema = z.object({
  nome: z
    .string()
    .min(1, "Nome é obrigatório")
    .max(255, "Nome deve ter no máximo 255 caracteres"),
  email: z
    .string()
    .email("Email inválido")
    .max(255, "Email deve ter no máximo 255 caracteres"),
  senha: z
    .string()
    .refine((val) => !val || val.length >= 6, {
      message: "Senha deve ter no mínimo 6 caracteres",
    })
    .optional(),
  ativo: z.boolean(),
  lojaId: z.number().int("Loja deve ser um número inteiro").optional(),
  roleId: z.number().int("Permissão deve ser um número inteiro"),
  isConferenteBase: z.boolean().optional(),
  isSupervisorProducao: z.boolean().optional(),
  setorIds: z.array(z.number().int()).optional(),
});

export const usuarioSchema = usuarioCreateSchema;

export const usuarioFilterSchema = z.object({
  nome: z.string().optional(),
  email: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (val === undefined || val === null || val.trim() === "") return true;
        return z.string().email().safeParse(val.trim()).success;
      },
      { message: "Informe um e-mail válido para filtrar" }
    ),
  lojaId: z.string().optional(),
  roleId: z.string().optional(),
  ativo: z
    .string()
    .optional()
    .refine(
      (val) =>
        val === undefined || val === "" || val === "true" || val === "false",
      { message: "Status deve ser Todos, Ativo ou Inativo" }
    ),
});

export const metaCreateSchema = z.object({
  tipo: z.string().refine((val) => val === "producao" || val === "entrega", {
    message: "Tipo deve ser 'producao' ou 'entrega'",
  }),
  tempo: z
    .string()
    .min(1, "Tempo é obrigatório")
    .regex(/^\d{2}:\d{2}:\d{2}$/, "Tempo deve estar no formato HH:MM:SS"),
  descricao: z
    .string()
    .min(1, "Descrição é obrigatória")
    .max(100, "Descrição deve ter no máximo 100 caracteres"),
  dataInicio: z.string().min(1, "Data de início é obrigatória"),
  dataFim: z.string().min(1, "Data de fim é obrigatória"),
  ativo: z.boolean(),
  lojaId: z
    .number()
    .int("Loja deve ser um número inteiro")
    .min(1, "Selecione uma loja"),
  usuarioIds: z
    .array(z.number().int())
    .min(1, "Selecione pelo menos um usuário"),
});

export const metaUpdateSchema = metaCreateSchema;

export const metaSchema = metaCreateSchema;

export const metaColetivaCreateSchema = z.object({
  inicializacaoNota: z
    .number()
    .min(0, "Nota deve ser no mínimo 0")
    .max(10, "Nota deve ser no máximo 10"),
  producaoNota: z
    .number()
    .min(0, "Nota deve ser no mínimo 0")
    .max(10, "Nota deve ser no máximo 10"),
  finalizacaoNota: z
    .number()
    .min(0, "Nota deve ser no mínimo 0")
    .max(10, "Nota deve ser no máximo 10"),
  tempoMeta: z
    .string()
    .min(1, "Tempo meta é obrigatório")
    .regex(/^\d{2}:\d{2}:\d{2}$/, "Tempo deve estar no formato HH:MM:SS"),
  tempoReal: z
    .string()
    .min(1, "Tempo real é obrigatório")
    .regex(/^\d{2}:\d{2}:\d{2}$/, "Tempo deve estar no formato HH:MM:SS"),
  errosProducao: z
    .number()
    .int("Erros deve ser um número inteiro")
    .min(0, "Erros deve ser no mínimo 0"),
  metaAtingida: z.number().min(0, "Meta atingida deve ser no mínimo 0"),
  observacao: z.string().optional(),
  lojaId: z.number().int("Loja deve ser um número inteiro"),
});

export const metaColetivaUpdateSchema = metaColetivaCreateSchema;

export const metaColetivaSchema = metaColetivaCreateSchema;

export type LojaFormData = z.infer<typeof lojaSchema>;
export type UsuarioFormData = z.infer<typeof usuarioSchema>;
export type UsuarioCreateData = z.infer<typeof usuarioCreateSchema>;
export type UsuarioUpdateData = z.infer<typeof usuarioUpdateSchema>;
export type UsuarioFilterData = z.infer<typeof usuarioFilterSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type MetaFormData = z.infer<typeof metaSchema>;
export type MetaCreateData = z.infer<typeof metaCreateSchema>;
export type MetaUpdateData = z.infer<typeof metaUpdateSchema>;
export type MetaColetivaFormData = z.infer<typeof metaColetivaSchema>;
export type MetaColetivaCreateData = z.infer<typeof metaColetivaCreateSchema>;
export type MetaColetivaUpdateData = z.infer<typeof metaColetivaUpdateSchema>;
